import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { LageBadge, MockBadge, Spinner } from "../components/ui";
import { useNavigate } from "react-router-dom";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

export default function Markets() {
  const { t } = useI18n();
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [sort, setSort] = useState("green_count");
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get("/markets");
      setMarkets(data.markets);
    } catch (e) {
      console.error("Markets load failed", e);
      setErr(t("common.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="p-10"><Spinner /></div>;
  if (err) return (
    <div className="p-10 text-center">
      <p className="text-sm text-signal-red font-mono">{err}</p>
      <button onClick={load} className="mt-3 text-xs font-mono uppercase tracking-widest2 text-signal-green hover:underline">
        {t("common.retry")}
      </button>
    </div>
  );

  const sortFns = {
    green_count:         (a, b) => b.green_count - a.green_count,
    avg_faktor:          (a, b) => a.avg_faktor - b.avg_faktor,
    lage_score:          (a, b) => b.lage_score - a.lage_score,
    absorption:          (a, b) => (a.absorption_days ?? 9999) - (b.absorption_days ?? 9999),
    demand_value_score:  (a, b) => (b.demand_value_score ?? 0) - (a.demand_value_score ?? 0),
    rent_scarcity:       (a, b) => (a.rent_per_100k ?? 99999) - (b.rent_per_100k ?? 99999),
    baujahr_median:      (a, b) => (a.baujahr_median ?? 9999) - (b.baujahr_median ?? 9999),
  };

  const rows = [...markets].sort(sortFns[sort]);

  // Miet-Liquidität (WP4): kompakte Unterzeile, nur aus tatsächlich vorhandenen
  // Feldern — rent_per_100k fehlt z. B. für Märkte ohne Bevölkerungswert.
  const rentLine = (m) => {
    const parts = [];
    if (m.rent_active_count != null) parts.push(`${m.rent_active_count} ${t("markets.rentListings")}`);
    if (m.rent_median_dom != null) parts.push(`Ø ${m.rent_median_dom}d`);
    if (m.rent_min_price != null) parts.push(`${t("markets.rentFrom")} €${Number(m.rent_min_price).toLocaleString()}`);
    if (m.rent_per_100k != null) parts.push(`${m.rent_per_100k}/100k`);
    return parts.length ? parts.join(" · ") : null;
  };

  return (
    <div className="reveal">
      <div className="px-6 md:px-10 pt-8 pb-5 border-b border-border bg-grid flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline">{t("markets.eyebrow")}</div>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tight">{t("markets.title")}</h1>
          <p className="mt-2 text-xs text-text-muted">{t("markets.countLabel", { count: markets.length })}</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            data-testid="markets-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-bg border border-border text-xs font-mono px-3 py-2 text-white"
          >
            <option value="green_count">{t("markets.sortGreenCount")}</option>
            <option value="avg_faktor">{t("markets.sortAvgFaktor")}</option>
            <option value="lage_score">{t("markets.sortLageScore")}</option>
            <option value="absorption">{t("markets.sortAbsorption")}</option>
            <option value="demand_value_score">{t("markets.sortDemandValue")}</option>
            <option value="rent_scarcity">{t("markets.sortRentScarcity")}</option>
            <option value="baujahr_median">{t("markets.sortBaujahr")}</option>
          </select>
          <MockBadge />
        </div>
      </div>

      <div className="border border-border">
        <div className="hidden md:grid md:grid-cols-12 text-[10px] font-mono uppercase tracking-widest2 text-text-muted border-b border-border bg-surface">
          <div className="col-span-1 p-3">{t("markets.colLage")}</div>
          <div className="col-span-2 p-3">{t("markets.colMarket")}</div>
          <div className="col-span-1 p-3 text-right">{t("markets.colScore")}</div>
          <div className="col-span-1 p-3 text-right">{t("markets.colFaktor")}</div>
          <div className="col-span-1 p-3 text-right">€/m²</div>
          <div className="col-span-1 p-3 text-right">{t("markets.colRentSqm")}</div>
          <div className="col-span-1 p-3 text-right">{t("markets.colBaujahr")}</div>
          <div className="col-span-1 p-3 text-right">{t("markets.colAbsorpt")}</div>
          <div className="col-span-1 p-3 text-right">{t("markets.colCycle")}</div>
          <div className="col-span-1 p-3 text-right">{t("markets.colGyr")}</div>
          <div className="col-span-1 p-3 text-right">{t("markets.colTrend")}</div>
        </div>
        {rows.map((m) => (
          <button
            key={m.code}
            onClick={() => nav(`/markets/${m.code}`)}
            data-testid={`market-row-${m.code}`}
            className="block w-full text-left border-b border-border hover:bg-surfaceHover transition-colors md:grid md:grid-cols-12 md:items-center"
          >
            {/* Mobile: stacked card */}
            <div className="p-3 space-y-1.5 md:hidden">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <LageBadge grade={m.lage_grade} />
                  <span className="text-sm text-white">{m.name}</span>
                </div>
                <span className={`text-[11px] font-mono ${m.cycle === "RISING" ? "text-signal-green" : m.cycle === "FALLING" ? "text-signal-red" : "text-text-secondary"}`}>{m.cycle}</span>
              </div>
              <div className="text-[10px] font-mono text-text-muted">{m.code} · {m.country}</div>
              {rentLine(m) && (
                <div className="text-[10px] font-mono text-text-muted">{t("markets.rentPrefix")} {rentLine(m)}</div>
              )}
              {(m.baujahr_median != null || m.altbau_share != null) && (
                <div className="text-[10px] font-mono text-text-muted" data-testid={`baujahr-line-${m.code}`}>
                  {t("markets.baujahrPrefix")} {m.baujahr_median != null ? m.baujahr_median : "—"}
                  {m.altbau_share != null && ` · ${m.altbau_share}% ${t("markets.altbauShort")}`}
                </div>
              )}
              <div className="flex items-center justify-between text-[10px] font-mono text-text-muted">
                <span>Faktor <span className="num text-white">{m.avg_faktor}</span></span>
                <span>Score <span className="num text-white">{m.lage_score}</span></span>
                <span>
                  <span className="text-signal-green num">{m.green_count}</span>/
                  <span className="text-signal-yellow num">{m.yellow_count}</span>/
                  <span className="text-signal-red num">{m.red_count}</span>
                </span>
              </div>
            </div>
            {/* Desktop: table row */}
            <div className="hidden md:contents">
              <div className="col-span-1 p-3"><LageBadge grade={m.lage_grade} /></div>
              <div className="col-span-2 p-3">
                <div className="text-sm text-white">{m.name}</div>
                <div className="text-[10px] font-mono text-text-muted">{m.code} · {m.country}</div>
                {rentLine(m) && (
                  <div className="text-[10px] font-mono text-text-muted">{t("markets.rentPrefix")} {rentLine(m)}</div>
                )}
              </div>
              <div className="col-span-1 p-3 num text-right text-white">{m.lage_score}</div>
              <div className="col-span-1 p-3 num text-right text-white">{m.avg_faktor}</div>
              <div className="col-span-1 p-3 num text-right text-text-secondary">€{m.med_price_sqm.toLocaleString()}</div>
              <div className="col-span-1 p-3 num text-right text-text-secondary">€{m.med_rent_sqm}</div>
              <div className="col-span-1 p-3 num text-right text-text-secondary" data-testid={`baujahr-cell-${m.code}`}>
                {m.baujahr_median != null ? m.baujahr_median : "—"}
                {m.altbau_share != null && (
                  <div className="text-[10px] font-mono text-text-muted">{m.altbau_share}% {t("markets.altbauShort")}</div>
                )}
              </div>
              <div className="col-span-1 p-3 num text-right text-text-secondary">{m.absorption_days != null ? `${m.absorption_days}d` : "—"}</div>
              <div className={`col-span-1 p-3 num text-right ${m.cycle === "RISING" ? "text-signal-green" : m.cycle === "FALLING" ? "text-signal-red" : "text-text-secondary"}`}>{m.cycle}</div>
              <div className="col-span-1 p-3 text-right">
                <span className="text-signal-green num">{m.green_count}</span>
                <span className="text-text-muted">/</span>
                <span className="text-signal-yellow num">{m.yellow_count}</span>
                <span className="text-text-muted">/</span>
                <span className="text-signal-red num">{m.red_count}</span>
              </div>
              <div className="col-span-1 p-3 text-right">
                {m.pop_trend === "decline" ? (
                  <span className="inline-flex items-center gap-1 text-signal-red text-[11px] font-mono"><ArrowDownRight size={12} /> ↓</span>
                ) : m.pop_trend === "stable" ? (
                  <span className="text-text-muted text-[11px] font-mono">→</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-signal-green text-[11px] font-mono"><ArrowUpRight size={12} /> ↑</span>
                )}
              </div>
            </div>
          </button>
        ))}
        {rows.length === 0 && (
          <div className="p-10 text-center text-text-muted font-mono text-xs">
            {t("markets.noRealData")} <code className="text-white">push_to_web.py</code>
            {" "}{t("markets.noRealData2")}
          </div>
        )}
      </div>
    </div>
  );
}
